import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

export const DOCUMENT_DRAG_MIME = "application/x-ca-project-document-ids";

/** Finder-style: if the dragged doc is selected, drag the whole visible selection. */
export function getDocumentIdsForDrag(
  draggedDocumentId: Id<"projectDocuments">,
  selectedIds: Set<string>,
  selectedVisibleIds: Id<"projectDocuments">[]
): Id<"projectDocuments">[] {
  if (selectedIds.has(String(draggedDocumentId)) && selectedVisibleIds.length > 0) {
    return selectedVisibleIds;
  }
  return [draggedDocumentId];
}

export function parseDocumentDragData(
  dataTransfer: DataTransfer
): Id<"projectDocuments">[] | null {
  const raw = dataTransfer.getData(DOCUMENT_DRAG_MIME);
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      !Array.isArray(parsed) ||
      parsed.length === 0 ||
      parsed.some((id) => typeof id !== "string")
    ) {
      return null;
    }
    return parsed as Id<"projectDocuments">[];
  } catch {
    return null;
  }
}

export function isDocumentDrag(dataTransfer: DataTransfer): boolean {
  return Array.from(dataTransfer.types).includes(DOCUMENT_DRAG_MIME);
}

export function isFileDrag(dataTransfer: DataTransfer): boolean {
  return Array.from(dataTransfer.types).includes("Files") && !isDocumentDrag(dataTransfer);
}

/** Browsers expose dropped folder entries as zero-byte files with an empty MIME type. */
export function isLikelyDroppedDirectoryEntry(file: File): boolean {
  return file.size === 0 && file.type === "";
}

export function partitionDroppedFiles(files: File[]): {
  uploadableFiles: File[];
  skippedDirectoryCount: number;
} {
  const uploadableFiles: File[] = [];
  let skippedDirectoryCount = 0;

  for (const file of files) {
    if (isLikelyDroppedDirectoryEntry(file)) {
      skippedDirectoryCount += 1;
      continue;
    }
    uploadableFiles.push(file);
  }

  return { uploadableFiles, skippedDirectoryCount };
}
